import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { EMPTY, Observable, catchError, expand, map, of, reduce, switchMap } from 'rxjs';

import { SAVING_ARENA_API_CONFIG } from '../../../../core/api/api.config';
import {
  FrontendBlog,
  FrontendBlogCategory,
  FrontendBlogCategoryTopStore,
  FrontendProduct,
} from '../../../../core/api/api.models';
import { blogBodyText, blogReadMinutes } from '../../../../core/api/blog-text';
import { SavingArenaApiService } from '../../../../core/api/saving-arena-api.service';
import { TrustedCmsHtmlPipe } from '../../../../core/api/trusted-cms-html.pipe';
import { SeoService } from '../../../../core/seo/seo.service';
import { SsrResponseService } from '../../../../core/seo/ssr-response.service';
import { breadcrumbSchemaFor, breadcrumbTrail } from '../../../../core/seo/breadcrumb-trail';
import { webPageSchema } from '../../../../core/seo/structured-data';
import { Breadcrumb } from '../../../../shared/breadcrumb/breadcrumb';
import { BlogPagination } from '../../components/blog-pagination/blog-pagination';
import { ProductGrid } from '../../../../shared/product-grid/product-grid';
import { LongformContent } from '../../../../shared/longform-content/longform-content';
import { BlogPostCard } from '../../../../shared/blog-post-card/blog-post-card';
import { AutoScrollRow } from '../../../../shared/auto-scroll-row/auto-scroll-row';


interface FaqItem {
  question: string;
  answer: string;
}

interface TopStoreItem {
  name: string;
  slug: string;
  logoUrl: string;
}

interface PageAuthor {
  name: string;
  role: string;
  avatarUrl: string;
  initials: string;
  bio: string;
  profileUrl: string;
}

interface CategoryView {
  id: string;
  name: string;
  slug: string;
  badgeLabel: string;
  heading: string;
  description: string;
  totalBlogs: number;
  iconHtml: SafeHtml;
  topStores: TopStoreItem[];
}

interface BlogPostView {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  imageUrl: string;
  storeLogoUrl: string;
  category: string;
  categorySlug: string;
  author: string;
  authorPath: string[];
  readTime: string;
  dateMs: number;
}

@Component({
  selector: 'app-blog-category-detail-page',
  imports: [
    Breadcrumb,
    RouterLink,
    BlogPagination,
    TrustedCmsHtmlPipe,
    ProductGrid,
    LongformContent,
    BlogPostCard,
    AutoScrollRow,
  ],
  templateUrl: './blog-category-detail-page.html',
  styleUrl: './blog-category-detail-page.scss',
})
export class BlogCategoryDetailPage {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(SavingArenaApiService);
  private readonly apiConfig = inject(SAVING_ARENA_API_CONFIG);
  private readonly destroyRef = inject(DestroyRef);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly seo = inject(SeoService);
  private readonly ssrResponse = inject(SsrResponseService);

  // Admin content (meta / short description / FAQs) for the current category, from
  // getBlogCategory(slug). Empty until loaded; the built-in defaults show meanwhile.
  private readonly heroHeadingOverride = signal('');
  private readonly heroDescriptionOverride = signal('');
  private readonly contentFaqs = signal<FaqItem[]>([]);

  // Hero H1: admin content headingH1 when set, otherwise the generated heading.
  protected readonly heroHeading = computed(
    () => this.heroHeadingOverride() || this.category().heading,
  );

  // Author of the category's most recent blog — powers the "Reviewed by" card.
  protected readonly pageAuthor = signal<PageAuthor | null>(null);

  // Distinct authors of the recent blogs — powers the "Expert Spotlight" card.
  private readonly spotlightExperts = signal<PageAuthor[]>([]);

  // Admin-curated products for this category (rendered in the home-page product design).
  protected readonly categoryProducts = signal<FrontendProduct[]>([]);

  // Long-form content (admin content form) rendered in the shared store-page design.
  protected readonly longContentHtml = signal('');

  // Real authors only. The card used to fall back to three stock-photo "experts" (Lee, Max, Ingrid)
  // who do not exist; with no real authors the card is not shown at all.
  protected readonly displayedExperts = computed<PageAuthor[]>(() => this.spotlightExperts());

  // The slug whose admin content / posts are currently applied. A section renders only once these
  // match the slug in the URL. After an in-app navigation the requests take a few hundred ms, and
  // until this guard the page filled that gap with built-in placeholder copy ("Featured Blog
  // Guides", the generic FAQs, the stock-photo experts) that then flipped to the real content. On a
  // full load nothing changes: the server renders with the data, and hydration reuses it.
  private readonly contentSlug = signal<string | null>(null);
  private readonly postsSlug = signal<string | null>(null);
  private readonly detailCategory = signal<CategoryView | null>(null);
  protected readonly contentReady = computed(
    () => this.contentSlug() === this.selectedCategorySlug(),
  );
  protected readonly postsReady = computed(() => this.postsSlug() === this.selectedCategorySlug());

  // FAQ list actually rendered: admin content FAQs when configured, else the built-in defaults.
  protected readonly displayedFaqs = computed(() =>
    this.contentFaqs().length > 0 ? this.contentFaqs() : this.faqItems,
  );

  protected readonly heroDescription = computed(
    () => this.heroDescriptionOverride() || this.category().description,
  );

  protected readonly currentPage = signal(1);
  protected readonly expandedFaqIndex = signal<number | null>(null);
  protected readonly pageSize = 15;
  protected readonly placeholderImageUrl = '/images/blog-placeholder.svg';
  protected readonly selectedCategorySlug = signal('');
  protected readonly categories = signal<CategoryView[]>([]);
  protected readonly blogPosts = signal<BlogPostView[]>([]);

  protected readonly crumbs = computed(() => {
    const category = this.category();
    const name = category.id === 'fallback' ? '' : category.name;
    return breadcrumbTrail(
      { label: 'Blog', path: '/blogs' },
      { label: 'Categories', path: '/blogs/categories' },
      ...(name ? [{ label: name }] : []),
    );
  });

  // The category's own detail response first (it carries the name, icon and top stores), then the
  // categories list. Never another category's data: this used to fall back to the first category
  // in the list, so a slow or unknown slug briefly wore a different category's name.
  protected readonly category = computed(() => {
    const detail = this.contentReady() ? this.detailCategory() : null;
    return detail ?? this.findCategoryBySlug(this.selectedCategorySlug()) ?? this.fallbackCategory();
  });

  // The API already returns only this category's posts (`/blogs?category=<slug>`).
  protected readonly categoryPosts = computed(() =>
    [...this.blogPosts()].sort((a, b) => b.dateMs - a.dateMs),
  );

  /**
   * The two newest posts, shown beside the hero copy.
   *
   * Two, not three: they render as the site's standard blog card now, and that card is designed for
   * roughly a quarter of the container. Three of them in the hero's 55% column would each be about
   * 226px wide — narrower than the card is drawn for anywhere else.
   */
  protected readonly featuredPosts = computed(() => this.categoryPosts().slice(0, 2));

  protected readonly pagedCategoryPosts = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize;
    return this.categoryPosts().slice(start, start + this.pageSize);
  });

  protected readonly totalPages = computed(() =>
    Math.max(1, Math.ceil(this.categoryPosts().length / this.pageSize)),
  );

  protected readonly categoryWidgets = computed(() => this.categories());

  protected readonly faqItems: FaqItem[] = [
    {
      question: 'How do I use a coupon code on SavingArena?',
      answer:
        "Browse our catalog of coupons and deals, click on the one you want to use, and copy the code. Then shop at the retailer's website and paste the code at checkout to apply your savings.",
    },
    {
      question: 'Are the coupons on SavingArena really free?',
      answer:
        'Yes! All coupons and deals on SavingArena are completely free to use. We partner with retailers to bring you exclusive discounts without any hidden fees or subscription requirements.',
    },
    {
      question: 'How often are the deals and coupons updated?',
      answer:
        'Our team refreshes deals daily to ensure you have access to the latest discounts. We remove expired codes automatically and add new ones as soon as they become available from our retail partners.',
    },
    {
      question: 'Do I need to create an account to use SavingArena?',
      answer:
        'No account is required. You can browse and copy coupon codes immediately. However, creating a free account gives you access to personalized deal alerts, price drop notifications, and a saved wishlist.',
    },
    {
      question: 'How does SavingArena make money?',
      answer:
        "We earn a small commission from retailers when you make a purchase using our links or codes. This doesn't affect the savings you get — in fact, it allows us to keep the service free for everyone.",
    },
    {
      question: "What should I do if a coupon code doesn't work?",
      answer:
        "If a code doesn't work, first double-check that you've entered it correctly. Some codes have minimum purchase requirements or exclusions. If it still fails, please report it so we can verify or replace it promptly.",
    },
  ];

  constructor() {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.selectedCategorySlug.set(params.get('categorySlug') ?? '');
      this.currentPage.set(1);
    });

    // Load the current category's admin content (meta / short description / FAQs).
    this.route.paramMap
      .pipe(
        map((params) => params.get('categorySlug') ?? ''),
        switchMap((slug) =>
          (slug
            ? this.api
                .getBlogCategory(slug)
                .pipe(
                  catchError((error: unknown) => {
                    this.ssrResponse.noteFetchFailure(error);
                    return of({ success: false, data: null });
                  }),
                )
            : of({ success: false, data: null })
          ).pipe(map((response) => ({ slug, response }))),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ slug, response }) => {
        this.applyCategoryContent(response.data);
        this.contentSlug.set(slug);
        // SEO runs where the category resolves, so the tags are correct during SSR (spec §5).
        this.applyCategorySeo(slug, response.data);
      });

    // This category's posts, as card payloads (no article bodies), re-fetched when the slug changes.
    this.route.paramMap
      .pipe(
        map((params) => params.get('categorySlug') ?? ''),
        switchMap((slug) =>
          (slug ? this.fetchCategoryBlogs(slug) : of([] as FrontendBlog[])).pipe(
            map((blogs) => ({ slug, blogs })),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ slug, blogs }) => {
        this.blogPosts.set(this.toBlogPostViews(blogs));
        this.postsSlug.set(slug);
      });

    this.loadCategories();
  }

  /**
   * Title / description / canonical / schema for a blog category.
   *
   * Everything is derived from the resolved category — a slug that resolves to nothing returns a
   * real 404 rather than a 200 "empty category" screen (spec §7).
   */
  private applyCategorySeo(slug: string, data: FrontendBlogCategory | null | undefined): void {
    if (!data) {
      this.ssrResponse.setNotFound();
      this.seo.apply({
        title: 'Category not found | SavingArena',
        description: 'The blog category you are looking for is not available on SavingArena.',
        noindex: true,
      });
      this.seo.setJsonLd([]);
      return;
    }

    const content =
      data['content'] && typeof data['content'] === 'object'
        ? (data['content'] as Record<string, unknown>)
        : {};

    const name = this.stripHtml(data.name || '');
    const metaTitle = String(content['metaTitle'] ?? '').trim();
    const metaDescription = this.stripHtml(String(content['metaDescription'] ?? '')).trim();
    const shortContent = this.stripHtml(
      String(content['shortContent'] ?? content['shortDescription'] ?? ''),
    ).trim();

    const title = metaTitle || `${name}: Coupon & Savings Guides | SavingArena`;
    const description =
      metaDescription ||
      shortContent ||
      `Read SavingArena guides in ${name}: coupon tips, sale timing and practical ways to spend less online.`;

    const path = `/blogs/${data.slug || slug}`;
    const url = this.seo.absolute(path);

    this.seo.apply({ title, description, canonicalPath: path });

    this.seo.setJsonLd([
      webPageSchema({ origin: this.seo.origin, url, name: title, description }),
      breadcrumbSchemaFor(this.crumbs(), (p) => this.seo.absolute(p)),
    ]);
  }

  private applyCategoryContent(data: FrontendBlogCategory | null | undefined): void {
    const content =
      data && data['content'] && typeof data['content'] === 'object'
        ? (data['content'] as Record<string, unknown>)
        : {};

    this.detailCategory.set(data ? this.toCategoryView(data) : null);
    this.heroHeadingOverride.set(this.stripHtml(String(content['headingH1'] ?? '')).trim());
    this.longContentHtml.set(String(content['longContent'] ?? '').trim());

    this.heroDescriptionOverride.set(
      this.stripHtml(String(content['shortContent'] ?? content['shortDescription'] ?? '')).trim(),
    );

    // Prefer FAQs authored in the content form; fall back to the standalone FAQ docs.
    const contentFaqs = Array.isArray(content['faqs'])
      ? (content['faqs'] as { question?: string; answer?: string }[])
      : [];
    const topLevelFaqs = Array.isArray(data?.faqs) ? data!.faqs : [];
    const source = contentFaqs.length > 0 ? contentFaqs : topLevelFaqs;
    this.contentFaqs.set(
      source
        .map((faq) => ({
          question: String(faq?.question ?? '').trim(),
          answer: String(faq?.answer ?? '').trim(),
        }))
        .filter((faq) => faq.question && faq.answer),
    );

    const authorRaw =
      data && data['recentBlogAuthor'] && typeof data['recentBlogAuthor'] === 'object'
        ? (data['recentBlogAuthor'] as Record<string, unknown>)
        : null;
    this.pageAuthor.set(authorRaw ? this.toPageAuthor(authorRaw) : null);

    const authorsRaw = Array.isArray(data?.['recentAuthors'])
      ? (data!['recentAuthors'] as Record<string, unknown>[])
      : [];
    this.spotlightExperts.set(
      authorsRaw
        .map((author) => this.toPageAuthor(author))
        .filter((author): author is PageAuthor => author !== null),
    );

    this.categoryProducts.set(
      Array.isArray(data?.['products']) ? (data!['products'] as FrontendProduct[]) : [],
    );
  }

  private toPageAuthor(author: Record<string, unknown>): PageAuthor | null {
    const name = String(author['name'] ?? '').trim();
    if (!name) {
      return null;
    }

    const profileImagePath = String(author['profileImagePath'] ?? '').trim();
    const slug = String(author['slug'] ?? '').trim();
    return {
      name,
      role: String(author['designation'] ?? '').trim(),
      avatarUrl: profileImagePath ? this.assetUrl(profileImagePath) : '',
      initials: this.getInitials(name),
      bio: this.truncateWords(
        this.stripHtml(String(author['bio'] ?? author['shortDescription'] ?? '')).trim(),
        45,
      ),
      profileUrl: String(author['profileUrl'] ?? '').trim() || (slug ? `/experts/${slug}` : ''),
    };
  }

  private truncateWords(value: string, maxWords: number): string {
    const words = value.split(/\s+/).filter(Boolean);
    if (words.length <= maxWords) {
      return value;
    }
    return `${words.slice(0, maxWords).join(' ')}…`;
  }

  private getInitials(value: string): string {
    const parts = value.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
      return '';
    }
    const first = parts[0][0] ?? '';
    const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : '';
    return `${first}${last}`.toUpperCase();
  }

  protected truncateText(value: string, maxLength: number): string {
    if (value.length <= maxLength) {
      return value;
    }

    return `${value.slice(0, maxLength).trimEnd()}...`;
  }

  protected toggleFaq(index: number): void {
    this.expandedFaqIndex.set(this.expandedFaqIndex() === index ? null : index);
  }

  protected onPageChange(page: number): void {
    this.currentPage.set(page);
  }

  private loadCategories(): void {
    this.api
      .getBlogCategories()
      .pipe(
        catchError(() => of({ success: false, data: [] })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        this.categories.set(response.data.map((category) => this.toCategoryView(category)));
      });
  }

  /**
   * Every post in one category, as card payloads. One request unless the category passes 100 posts.
   * This used to page through EVERY post on the site, full article bodies included, and filter in
   * the browser; the transfer cache then embedded all of it in this page's HTML.
   */
  private fetchCategoryBlogs(slug: string): Observable<FrontendBlog[]> {
    const page = (n: number) =>
      this.api.getBlogs({ page: n, limit: 100, category: slug, fields: 'card' });

    return page(1).pipe(
      expand((response) =>
        response.pagination.page < response.pagination.pages
          ? page(response.pagination.page + 1)
          : EMPTY,
      ),
      reduce((all, response) => all.concat(response.data), [] as FrontendBlog[]),
      catchError(() => of([] as FrontendBlog[])),
    );
  }

  private toCategoryView(category: FrontendBlogCategory): CategoryView {
    const name = category.name;
    const slug = category.slug || this.slugify(name);

    return {
      id: category.id,
      name,
      slug,
      badgeLabel: name.toUpperCase(),
      heading: this.categoryHeading(category),
      description: this.categoryDescription(category),
      totalBlogs: category.totalBlogs ?? this.countFromUnknown(category.blogs),
      iconHtml: this.categoryIconHtml(category),
      topStores: this.toTopStores(category.topStores),
    };
  }

  private toTopStores(stores: FrontendBlogCategoryTopStore[] | undefined): TopStoreItem[] {
    if (!Array.isArray(stores)) {
      return [];
    }

    return stores
      .map((store) => ({
        name: store.name || '',
        slug: (store.slug || '').trim(),
        logoUrl: this.assetUrl(store.logoImagePath),
      }))
      .filter((store) => store.slug);
  }

  private toBlogPostViews(blogs: FrontendBlog[]): BlogPostView[] {
    return blogs.map((blog) => {
      const category = blog.category;
      const categoryName = category?.name ?? 'Shopping Tips';
      const categorySlug = category?.slug || this.slugify(categoryName);
      const dateMs = this.blogDateMs(blog);
      const readTime = `${this.readTimeMinutes(blog)} Min`;
      const store = Array.isArray(blog.stores) ? blog.stores[0] : undefined;
      const storeLogo =
        store && typeof store.logoImagePath === 'string' ? store.logoImagePath.trim() : '';
      const author = this.authorName(blog);

      return {
        id: blog.id,
        title: blog.name,
        slug: blog.slug,
        // `shortDescription` is rich text from the admin, so it has to be stripped like the
        // body is — it was printed raw and readers saw literal <p> tags in the card.
        excerpt:
          this.excerptFromContent(blog.shortDescription) ||
          this.excerptFromContent(blogBodyText(blog)) ||
          blog.name,
        imageUrl: this.assetUrl(blog.thumbnailImagePath) || this.placeholderImageUrl,
        storeLogoUrl: storeLogo ? this.assetUrl(storeLogo) : '',
        category: categoryName,
        categorySlug,
        author,
        authorPath: ['/experts', this.slugify(author)],
        readTime,
        dateMs,
      } satisfies BlogPostView;
    });
  }

  private findCategoryBySlug(slug: string): CategoryView | null {
    const categories = this.categories();
    const exactMatch = categories.find((category) => category.slug === slug);

    if (exactMatch) {
      return exactMatch;
    }

    if (slug === 'seasonal-deals') {
      return categories.find((category) => category.slug === 'seasonal-sales') ?? null;
    }

    return null;
  }

  private categoryHeading(category: FrontendBlogCategory): string {
    return (
      this.stringFromUnknown(category['h1']) ||
      this.stringFromUnknown(category['heading']) ||
      this.stringFromUnknown(category['title']) ||
      this.stringFromRecord(category.content, ['h1', 'heading', 'title']) ||
      `Featured ${category.name} Guides`
    );
  }

  private categoryDescription(category: FrontendBlogCategory): string {
    return (
      this.stringFromUnknown(category['shortDescription']) ||
      this.stringFromUnknown(category['description']) ||
      this.stringFromUnknown(category['summary']) ||
      this.stringFromRecord(category.content, [
        'shortDescription',
        'description',
        'summary',
        'metaDescription',
      ]) ||
      `Explore featured ${category.name.toLowerCase()} guides with coupon tips, sale insights, and proven strategies to save more on online shopping deals.`
    );
  }

  private categoryIconHtml(category: FrontendBlogCategory): SafeHtml {
    if (category.iconCode?.trim()) {
      return this.sanitizer.bypassSecurityTrustHtml(category.iconCode);
    }

    return this.sanitizer.bypassSecurityTrustHtml(
      '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path></svg>',
    );
  }

  private stringFromRecord(record: Record<string, unknown> | undefined, keys: string[]): string {
    if (!record) {
      return '';
    }

    for (const key of keys) {
      const value = this.stringFromUnknown(record[key]);

      if (value) {
        return value;
      }
    }

    return '';
  }

  private stringFromUnknown(value: unknown): string {
    return typeof value === 'string' ? this.stripHtml(value) : '';
  }

  private countFromUnknown(value: unknown): number {
    if (Array.isArray(value)) {
      return value.length;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    return 0;
  }

  private blogDateMs(blog: FrontendBlog): number {
    return this.dateMs(blog.publishAt) || this.dateMs(blog.updatedAt);
  }

  private dateMs(value: string | undefined): number {
    if (!value) {
      return 0;
    }

    const dateMs = new Date(value).getTime();

    return Number.isNaN(dateMs) ? 0 : dateMs;
  }

  private readTimeMinutes(blog: FrontendBlog): number {
    return blogReadMinutes(blog);
  }

  private excerptFromContent(value?: string): string {
    if (!value) {
      return '';
    }

    const text = this.stripHtml(value);

    if (text.length <= 150) {
      return text;
    }

    return `${text.slice(0, 150).trimEnd()}...`;
  }

  private stripHtml(value: string): string {
    return value
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private authorName(blog: FrontendBlog): string {
    const author = blog['author'];

    if (typeof author === 'string') {
      return author;
    }

    if (author && typeof author === 'object' && 'name' in author) {
      const name = (author as { name?: unknown }).name;
      return typeof name === 'string' ? name : 'SavingArena Team';
    }

    return 'SavingArena Team';
  }

  private assetUrl(path: string | undefined): string {
    if (!path) {
      return '';
    }

    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    const assetBaseUrl = this.apiConfig.assetBaseUrl.replace(/\/$/, '');
    const assetPath = path.startsWith('/') ? path : `/${path}`;

    return `${assetBaseUrl}${assetPath}`;
  }

  private slugify(value: string): string {
    return value
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  private fallbackCategory(): CategoryView {
    return {
      id: 'fallback',
      name: 'Blog',
      slug: 'blog',
      badgeLabel: 'BLOG',
      heading: 'Featured Blog Guides',
      description:
        'Explore featured guides with coupon tips, sale insights, and proven savings strategies.',
      totalBlogs: 0,
      iconHtml: this.categoryIconHtml({ id: 'fallback', name: 'Blog', slug: 'blog' }),
      topStores: [],
    };
  }
}
